// Въпросите около черновите на началния екран (и на таблото) — общият
// потвърждаващ диалог (components/confirm), думите — от `DraftTexts` на типа:
//  • „Да продължите ли … ?" (при „+ Нова", докато имам незавършена) — два
//    избора-карти: продължи (фокусът) / изтрий и започни нова;
//  • „Да поемете ли … на …?" (заета чернова) — кой я държи, какво става с
//    неговите промени и конкретният бутон „Поеми от Мария".
// `DraftHomeModals` рендира двата наведнъж от `useHomeDrafts`.
import type { ReactNode } from "react";
import dayjs from "dayjs";
import { FileTextOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { InventoryDraft } from "../../../../api/inventoryDrafts";
import { ConfirmDialog } from "../confirm/ConfirmDialog";
import { formatMoney } from "../numbers";
import type { DraftSummary } from "./draftModel";
import { labelParams, type DraftTexts } from "./draftTexts";
import type { HomeDrafts } from "./useHomeDrafts";

export const DraftAskModal = ({
  mine,
  open,
  texts,
  onClose,
  onDiscard,
}: {
  mine: DraftSummary | null;
  open: boolean;
  texts: DraftTexts;
  onClose: () => void;
  onDiscard: () => void;
}) => {
  const { t } = useTranslation();
  const label = mine?.label || null;
  const value: ReactNode = mine
    ? [t(texts.linesLabel, { count: mine.linesCount }), mine.total != null ? formatMoney(mine.total) : null]
        .filter(Boolean)
        .join(" · ")
    : null;
  return (
    <ConfirmDialog
      open={open && mine != null}
      intent="info"
      icon={<FileTextOutlined />}
      title={label && texts.askTitleWithLabel ? t(texts.askTitleWithLabel, labelParams(label)) : t(texts.askTitle)}
      explanation={t(texts.askText)}
      preview={
        mine
          ? {
              caption: t(texts.askCaption),
              items: [
                {
                  key: mine.key,
                  label: label ?? t(texts.labelFallback),
                  hint: mine.updatedAt
                    ? t("inventoryPage.homeDraftUpdated", { when: dayjs(mine.updatedAt).format("DD.MM HH:mm") })
                    : undefined,
                  value,
                },
              ],
            }
          : undefined
      }
      choices={[
        {
          key: "continue",
          label: t(texts.askContinue),
          hint: t(texts.askContinueHint),
          primary: true,
          onSelect: () => {
            onClose();
            mine?.onOpen();
          },
        },
        {
          key: "discard",
          label: t(texts.askDiscard),
          hint: t(texts.askDiscardHint),
          tone: "danger",
          onSelect: onDiscard,
        },
      ]}
      onCancel={onClose}
    />
  );
};

export const DraftLockModal = ({
  draft,
  busy,
  texts,
  onClose,
  onTakeOver,
}: {
  draft: InventoryDraft | null;
  busy: boolean;
  texts: DraftTexts;
  onClose: () => void;
  onTakeOver: () => void;
}) => {
  const { t } = useTranslation();
  const name = draft?.lockedByName || draft?.ownerName || "?";
  const first = name.split(/\s+/)[0];
  return (
    <ConfirmDialog
      open={draft != null}
      intent="warning"
      icon={<UserSwitchOutlined />}
      title={t(texts.lockTitle, { name })}
      explanation={t(texts.lockText, { name, first })}
      preview={
        draft
          ? {
              caption: t("inventoryPage.cfmLockCaption"),
              items: [
                {
                  key: draft.id,
                  label: draft.supplierName || t(texts.labelFallback),
                  hint: draft.updatedAt
                    ? t("inventoryPage.homeDraftUpdated", { when: dayjs(draft.updatedAt).format("DD.MM HH:mm") })
                    : undefined,
                  value: t(texts.linesLabel, { count: draft.linesCount }),
                },
              ],
            }
          : undefined
      }
      confirmLabel={t("inventoryPage.cfmLockConfirm", { first })}
      busy={busy}
      onConfirm={onTakeOver}
      onCancel={onClose}
    />
  );
};

// Двата въпроса на екран с черновите на един тип
export const DraftHomeModals = ({
  drafts,
  texts,
}: {
  drafts: Pick<HomeDrafts, "mine" | "ask" | "lock">;
  texts: DraftTexts;
}) => (
  <>
    <DraftAskModal
      mine={drafts.mine}
      open={drafts.ask.open}
      texts={texts}
      onClose={drafts.ask.close}
      onDiscard={drafts.ask.discard}
    />
    <DraftLockModal
      draft={drafts.lock.draft}
      busy={drafts.lock.busy}
      texts={texts}
      onClose={drafts.lock.close}
      onTakeOver={drafts.lock.confirm}
    />
  </>
);
